import React, { useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import '../../UserManagement.css';
import { useAuth } from '../../AuthContext';
import { getUserTeamIds } from '../../utils/roleUtils';

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [importedUsers, setImportedUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showSyncWarningModal, setShowSyncWarningModal] = useState(false);
  const [showSaveConfirmationModal, setShowSaveConfirmationModal] = useState(false);
  const [form, setForm] = useState({ 
    email: '', 
    full_name: '', 
    phone: '', 
    team_ids: [],  // Changed to array for multi-team support
    role: 'team_lead', 
    password: '' 
  });
  const [originalForm, setOriginalForm] = useState({ 
    email: '', 
    full_name: '', 
    phone: '', 
    team_ids: [],  // Changed to array for multi-team support
    role: 'team_lead', 
    password: '' 
  });
  const [isNew, setIsNew] = useState(false);
  const [copiedCell, setCopiedCell] = useState(null); // { userId, field }
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' or 'imported'
  
  // Filter and search states for imported users
  const [searchTerm, setSearchTerm] = useState('');
  const [teamFilter, setTeamFilter] = useState([]);
  const [phoneFilter, setPhoneFilter] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);
  const [sortBy, setSortBy] = useState('id'); // 'name', 'team', or 'id'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [showSortMenu, setShowSortMenu] = useState(false);

  const { supabase, user } = useAuth();

  // Helper function to get consistent color index for a team ID
  const getTeamColorIndex = (teamId) => {
    // Use team ID modulo 6 to get a consistent color (0-5)
    return teamId % 6;
  };

  // Helper function to check if we're editing an imported user
  const isEditingImportedUser = () => {
    if (isNew) return false; // New users are always added to dashboard users
    // Check if the user being edited is in the importedUsers array
    return importedUsers.some(u => u.id === editingUser);
  };

  // Helper function to check if current user is admin
  const isCurrentUserAdmin = () => {
    return user?.role === 'admin';
  };

  // Helper function to check if form has changes
  const hasFormChanges = () => {
    if (isNew) return true; // New users always have changes

    // The password field always starts blank and blank means "keep current",
    // so any typed value is itself a change - there is nothing to compare it to.
    if (!isEditingImportedUser() && form.password) return true;

    // For editing existing users, compare current form with original form
    // For imported users, don't compare password since it's not editable
    const fieldsToCompare = isEditingImportedUser()
      ? ['email', 'full_name', 'phone', 'team_ids']
      : ['email', 'full_name', 'phone', 'team_ids', 'role'];
    
    return fieldsToCompare.some(field => {
      const currentValue = form[field];
      const originalValue = originalForm[field];
      
      // Special handling for team_ids array comparison
      if (field === 'team_ids') {
        const current = Array.isArray(currentValue) ? currentValue.sort() : [];
        const original = Array.isArray(originalValue) ? originalValue.sort() : [];
        return JSON.stringify(current) !== JSON.stringify(original);
      }
      
      return (currentValue || '') !== (originalValue || '');
    });
  };

  // Helper function to get current users based on active tab
  const getCurrentUsers = () => {
    let currentUsers = activeTab === 'dashboard' ? users : importedUsers;
    
    // Apply filtering and sorting for both tabs
    // Apply search filter - search by name or ID
    if (searchTerm) {
      currentUsers = currentUsers.filter(user => 
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.id?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply team filter
    if (teamFilter && teamFilter.length > 0) {
      currentUsers = currentUsers.filter(user => {
        // Check if any of user's teams match the filter
        if (user.teams && Array.isArray(user.teams)) {
          return user.teams.some(team => teamFilter.includes(team.name));
        }
        return false;
      });
    }
    
    // Apply phone filter
    if (phoneFilter && phoneFilter.length > 0) {
      currentUsers = currentUsers.filter(user => 
        phoneFilter.includes(user.twilio_phone_number || '')
      );
    }
    
    // Apply role filter
    if (roleFilter && roleFilter.length > 0) {
      currentUsers = currentUsers.filter(user => 
        roleFilter.includes(user.role || '')
      );
    }
    
    // Apply sorting
    currentUsers.sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'name') {
        aValue = a.full_name || '';
        bValue = b.full_name || '';
      } else if (sortBy === 'team') {
        aValue = a.teams?.name || '';
        bValue = b.teams?.name || '';
      } else if (sortBy === 'id') {
        aValue = a.id || 0;
        bValue = b.id || 0;
      }
      
      if (sortBy === 'id') {
        // For ID sorting, use numeric comparison
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        // For string sorting, use localeCompare
        if (sortOrder === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
    });
    
    return currentUsers;
  };

  // Helper functions to get unique values for filters
  const getUniqueTeams = () => {
    const currentUsers = activeTab === 'dashboard' ? users : importedUsers;
    const teamSet = new Set();
    
    currentUsers.forEach(user => {
      if (user.teams && Array.isArray(user.teams)) {
        user.teams.forEach(team => {
          if (team && team.name) {
            teamSet.add(team.name);
          }
        });
      }
    });
    
    return Array.from(teamSet).sort();
  };

  const getUniqueRoles = () => {
    const currentUsers = activeTab === 'dashboard' ? users : importedUsers;
    return [...new Set(currentUsers.map(user => user.role).filter(Boolean))];
  };

  const getUniquePhoneNumbers = () => {
    const currentUsers = activeTab === 'dashboard' ? users : importedUsers;
    return [...new Set(currentUsers.map(user => user.twilio_phone_number).filter(Boolean))];
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTeamFilter([]);
    setPhoneFilter([]);
    setRoleFilter([]);
    setSortBy(activeTab === 'dashboard' ? 'name' : 'id');
    setSortOrder('asc');
  };

  const handleSortToggle = () => {
    setShowSortMenu(!showSortMenu);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setShowSortMenu(false);
  };

  const getSortDisplay = (field) => {
    const isActive = sortBy === field;
    const arrow = isActive ? (sortOrder === 'desc' ? '↓' : '↑') : '';
    
    const labels = {
      'name': 'Name',
      'team': 'Team',
      'id': 'ID'
    };
    
    return (
      <span>
        {labels[field]}
        {arrow && <span className="sort-arrow">{arrow}</span>}
      </span>
    );
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch dashboard users - just get basic user data
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('*');
      
      if (usersError) throw usersError;
      
      // Fetch imported users - just get basic user data
      const { data: importedUsersData, error: importedUsersError } = await supabase
        .from('imported_users')
        .select('*');
      
      if (importedUsersError) throw importedUsersError;
      
      // Fetch all teams for the dropdown directly from Supabase
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('id, name')
        .order('name', { ascending: true });
      
      if (teamsError) {
        throw teamsError;
      }
      
      // Create a teams map for quick lookup
      const teamsMap = {};
      if (teamsData) {
        teamsData.forEach(team => {
          teamsMap[team.id] = team;
        });
      }
      
      // Enrich users with team names based on team_ids
      const enrichUsers = (users) => {
        return users.map(user => {
          const teamIds = getUserTeamIds(user);
          const userTeams = teamIds.map(tid => teamsMap[tid]).filter(Boolean);
          
          return {
            ...user,
            teams: userTeams,
            teamsDisplay: userTeams.length > 0 
              ? userTeams.map(t => t.name).join(', ') 
              : '—'
          };
        });
      };
      
      setUsers(enrichUsers(usersData || []));
      setImportedUsers(enrichUsers(importedUsersData || []));
      setTeams(teamsData || []);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
      setUsers([]);
      setImportedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncClick = () => {
    setShowSyncWarningModal(true);
  };

  const closeSyncWarningModal = () => {
    setShowSyncWarningModal(false);
  };

  const confirmSyncOrganization = async () => {
    setShowSyncWarningModal(false);
    setSyncing(true);
    setError('');
    try {
      // Get Supabase session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No authentication token available');
      }

      // Call the sync endpoint directly with Supabase token
      const response = await fetch('/organization/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Sync failed with status ${response.status}`);
      }

      const result = await response.json();
      
      // Show success message briefly
      setSuccessMessage('Organization sync completed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh the user data after successful sync
      await fetchUsers();
    } catch (err) {
      setError('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { 
    fetchUsers(); 
  }, []);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if clicking on sort button itself  
      if (event.target.closest('.sort-btn')) {
        return; // Let the button handle its own toggle
      }
      
      // Close dropdowns if clicking outside their containers
      if (showSortMenu && !event.target.closest('.sort-container')) {
        setShowSortMenu(false);
      }
    };

    if (showSortMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSortMenu]);

  // Update default sorting when switching tabs
  useEffect(() => {
    const defaultSort = activeTab === 'dashboard' ? 'name' : 'id';
    if (sortBy !== defaultSort) {
      setSortBy(defaultSort);
      setSortOrder('asc');
    }
  }, [activeTab]);

  const copyToClipboard = (text, userId, field) => {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedCell({ userId, field });
        setTimeout(() => setCopiedCell(null), 2000);
      }).catch(() => {
        // Silently fail - user will notice text wasn't copied
      });
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setIsNew(false);
    
    // Get user's team IDs using the helper function
    const userTeamIds = getUserTeamIds(user);
    
    const formData = {
      email: user.email || '',
      full_name: user.full_name || '',
      phone: user.twilio_phone_number || '',
      team_ids: userTeamIds,  // Now an array
      role: user.role || 'agent',
      password: ''
    };
    setForm(formData);
    setOriginalForm(formData); // Store original data for comparison
    setShowEditModal(true);
  };

  const handleAdd = () => {
    setEditingUser(null);
    setIsNew(true);
    const formData = { 
      email: '', 
      full_name: '', 
      phone: '', 
      team_ids: [],  // Start with empty array
      role: 'team_lead', 
      password: '' 
    };
    setForm(formData);
    setOriginalForm(formData); // Store original data for comparison
    setShowEditModal(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setError('');
    try {
      // Determine which table the user belongs to
      const isImportedUser = importedUsers.some(u => u.id === userToDelete.id);
      const endpoint = isImportedUser ? `/api/imported-users/${userToDelete.id}` : `/api/users/${userToDelete.id}`;
      
      const res = await fetch(endpoint, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Failed to delete user');
      } else {
        fetchUsers();
        setShowDeleteModal(false);
        setUserToDelete(null);
      }
    } catch (e) {
      setError('Failed to delete user');
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    setShowSaveConfirmationModal(true);
  };

  const closeSaveConfirmationModal = () => {
    setShowSaveConfirmationModal(false);
  };

  const confirmSave = async () => {
    setShowSaveConfirmationModal(false);
    setError('');
    
    try {
      if (isNew) {
        // Create new user via API
        const body = {
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          phone: form.phone,
          team_ids: form.team_ids && form.team_ids.length > 0 ? form.team_ids : null,
          role: form.role || 'agent'
        };
        
        const res = await fetch('/api/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || 'Failed to create user');
          return;
        }
        
        setSuccessMessage('User created successfully!');
      } else if (isEditingImportedUser()) {
        // Update imported user directly via Supabase
        // Note: imported_users can only have one team, so use team_id (singular)
        // IMPORTANT: Do NOT include team_ids - only team_id (singular)
        
        // Extract team_id from form.team_ids array (take first element, or null)
        const teamId = form.team_ids && Array.isArray(form.team_ids) && form.team_ids.length > 0 
          ? form.team_ids[0] 
          : null;
        
        // Build updateData object explicitly - ONLY include fields that exist in imported_users table
        // List of valid fields for imported_users: id, email, full_name, twilio_phone_number, team_id, role, phone_number
        // CRITICAL: Do NOT include team_ids - only team_id (singular)
        
        // Build the update object directly with only allowed fields - no intermediate objects
        const filteredUpdateData = {};
        
        // Only add fields that are defined
        if (form.email !== undefined && form.email !== null && form.email !== '') {
          filteredUpdateData.email = form.email;
        }
        if (form.full_name !== undefined && form.full_name !== null && form.full_name !== '') {
          filteredUpdateData.full_name = form.full_name;
        }
        // twilio_phone_number can be empty string, so include it if defined
        if (form.phone !== undefined) {
          filteredUpdateData.twilio_phone_number = form.phone || '';
        }
        // team_id can be null, so always include it
        filteredUpdateData.team_id = teamId;
        
        // ABSOLUTE SAFETY: Remove team_ids if it somehow got in there
        delete filteredUpdateData.team_ids;
        
        // Verify no team_ids before sending
        if ('team_ids' in filteredUpdateData) {
          console.error('[UserManagement] ERROR: team_ids found in updateData! Removing it.');
          delete filteredUpdateData.team_ids;
        }
        
        const { error } = await supabase
          .from('imported_users')
          .update(filteredUpdateData)
          .eq('id', editingUser);
          
        if (error) {
          console.error('[UserManagement] Failed to update imported user:', error);
          console.error('[UserManagement] Update data that was sent:', JSON.stringify(filteredUpdateData));
          console.error('[UserManagement] Keys in update data:', Object.keys(filteredUpdateData));
          setError('Failed to update imported user: ' + error.message);
          return;
        }
        
        setSuccessMessage('User updated successfully!');
      } else {
        // Update dashboard user via API
        const body = {
          email: form.email,
          full_name: form.full_name,
          phone: form.phone,
          team_ids: form.team_ids && form.team_ids.length > 0 ? form.team_ids : null,
          role: form.role
        };
        
        // Include password only if provided
        if (form.password) {
          body.password = form.password;
        }
        
        const res = await fetch(`/api/users/${editingUser}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || 'Failed to update user');
          return;
        }
        
        setSuccessMessage('User updated successfully!');
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Success - close modal and refresh data
      setShowEditModal(false);
      setEditingUser(null);
      setIsNew(false);
      setForm({ email: '', full_name: '', phone: '', team_ids: [], role: 'agent', password: '' });
      setOriginalForm({ email: '', full_name: '', phone: '', team_ids: [], role: 'agent', password: '' });
      fetchUsers();
    } catch (e) {
      setError('Failed to save user: ' + e.message);
    }
  };

  const closeModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setIsNew(false);
    setForm({ email: '', full_name: '', phone: '', team_ids: [], role: 'agent', password: '' });
    setOriginalForm({ email: '', full_name: '', phone: '', team_ids: [], role: 'agent', password: '' });
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h1 className="user-management-title">User Management</h1>
        <div className="header-buttons">
          <button onClick={fetchUsers} className="refresh-button">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            Refresh Page
          </button>
          <button 
            onClick={handleSyncClick} 
            className="sync-button"
            disabled={syncing}
          >
            {syncing ? (
              <Spin size="small" style={{ color: '#8b5cf6' }} />
            ) : (
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
              </svg>
            )}
            {syncing ? 'Syncing...' : 'Sync Organization'}
          </button>
          <button onClick={handleAdd} className="add-user-button">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            Add User
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard Users ({users.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'imported' ? 'active' : ''}`}
          onClick={() => setActiveTab('imported')}
        >
          Imported Users ({importedUsers.length})
        </button>
      </div>

      {/* Filter Controls for Both Tabs */}
      {(activeTab === 'imported' || activeTab === 'dashboard') && (
        <div className="filter-controls">
          <div className="filter-row">
            <div className="search-container">
              <div className="search-input-wrapper">
                <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.6 21L13.3 14.7C12.8 15.1 12.225 15.4167 11.575 15.65C10.925 15.8833 10.2333 16 9.5 16C7.68333 16 6.146 15.3707 4.888 14.112C3.63 12.8533 3.00067 11.316 3 9.5C3 7.68333 3.62933 6.146 4.888 4.888C6.14667 3.63 7.684 3.00067 9.5 3C11.3167 3 12.854 3.62933 14.112 4.888C15.37 6.14667 15.9993 7.684 16 9.5C16 10.2333 15.8833 10.925 15.65 11.575C15.4167 12.225 15.1 12.8 14.7 13.3L21 19.6L19.6 21ZM9.5 14C10.75 14 11.8127 13.5627 12.688 12.688C13.5633 11.8133 14.0007 10.7507 14 9.5C14 8.25 13.5627 7.18733 12.688 6.312C11.8133 5.43667 10.7507 4.99933 9.5 5C8.25 5 7.18733 5.43733 6.312 6.312C5.43667 7.18667 4.99933 8.24933 5 9.5C5 10.75 5.43733 11.8127 6.312 12.688C7.18667 13.5633 8.24933 14.0007 9.5 14Z" fill="currentColor"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search by name or ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="search-clear-btn"
                    title="Clear search"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            
            <div className="filter-group">
              <Select
                style={{ width: 200 }}
                placeholder="Filter by team(s)"
                value={teamFilter || []}
                onChange={(value) => setTeamFilter(value || [])}
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                maxTagCount={2}
                maxTagTextLength={10}
              >
                {getUniqueTeams().map(team => (
                  <Select.Option key={team} value={team}>{team}</Select.Option>
                ))}
              </Select>
              
              <Select
                style={{ width: 200 }}
                placeholder="Filter by phone number(s)"
                value={phoneFilter || []}
                onChange={(value) => setPhoneFilter(value || [])}
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                maxTagCount={2}
                maxTagTextLength={10}
              >
                {getUniquePhoneNumbers().map(phone => (
                  <Select.Option key={phone} value={phone}>{phone}</Select.Option>
                ))}
              </Select>
              
              <Select
                style={{ width: 200 }}
                placeholder="Filter by role(s)"
                value={roleFilter || []}
                onChange={(value) => setRoleFilter(value || [])}
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                maxTagCount={2}
                maxTagTextLength={10}
              >
                {getUniqueRoles().map(role => (
                  <Select.Option key={role} value={role}>
                    {role === 'admin' ? 'Admin' : role === 'team_lead' ? 'Team Lead' : role === 'agent' ? 'Agent' : 'Team Lead'}
                  </Select.Option>
                ))}
              </Select>
              
              {/* Sort Menu */}
              <div className="sort-container">
                            <button 
              className={`sort-btn ${showSortMenu ? 'active' : ''}`}
              onClick={handleSortToggle}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 18H9V16H3V18ZM3 6V8H21V6H3ZM3 13H15V11H3V13Z" fill="currentColor"/>
              </svg>
              Sort
            </button>
                
                {showSortMenu && (
                  <div className="sort-dropdown">
                    <div className="sort-content">
                      <button 
                        onClick={() => handleSort('name')}
                        className={sortBy === 'name' ? 'sort-option active' : 'sort-option'}
                      >
                        {getSortDisplay('name')}
                      </button>
                      <button 
                        onClick={() => handleSort('team')}
                        className={sortBy === 'team' ? 'sort-option active' : 'sort-option'}
                      >
                        {getSortDisplay('team')}
                      </button>
                      {activeTab === 'imported' && (
                        <button 
                          onClick={() => handleSort('id')}
                          className={sortBy === 'id' ? 'sort-option active' : 'sort-option'}
                        >
                          {getSortDisplay('id')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="filter-results">
            Showing {getCurrentUsers().length} of {activeTab === 'dashboard' ? users.length : importedUsers.length} {activeTab === 'dashboard' ? 'dashboard' : 'imported'} users
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-text">Loading users...</div>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
          <thead>
              <tr>
                <th>Name & ID</th>
                <th>Twilio Phone Number</th>
                <th>Team</th>
                <th>Role</th>
                <th>Actions</th>
            </tr>
          </thead>
          <tbody>
              {getCurrentUsers().map((user) => (
                <tr key={user.id}>
                  <td className="name-cell">
                    <div className="user-name">{user.full_name || '—'}</div>
                    <div 
                      className={`user-id ${copiedCell?.userId === user.id && copiedCell?.field === 'user_id' ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(user.id, user.id, 'user_id')}
                      title="Click to copy User ID"
                    >
                      {copiedCell?.userId === user.id && copiedCell?.field === 'user_id' ? 'Copied!' : user.id}
                    </div>
                  </td>
                  <td className="phone-cell">
                    <div className="user-phone">{user.twilio_phone_number || '—'}</div>
                  </td>
                  <td className="team-cell">
                    {user.teams && user.teams.length > 0 ? (
                      <div className="teams-badges-container">
                        {user.teams.map((team, idx) => (
                          <span 
                            key={idx} 
                            className="team-badge" 
                            data-color-index={getTeamColorIndex(team.id)}
                            title={`Team ID: ${team.id}`}
                          >
                            {team.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="team-name">—</div>
                    )}
                  </td>
                  <td>
                    <span className={`role-badge ${user.role || 'team_lead'}`}>
                      {user.role === 'admin' ? 'Admin' : user.role === 'team_lead' ? 'Team Lead' : user.role === 'agent' ? 'Agent' : 'Team Lead'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="action-btn edit-btn"
                      onClick={() => handleEdit(user)}
                    >
                      Edit
                    </button>
                    {activeTab === 'dashboard' && (
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteClick(user)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Edit/Add User Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                {isNew ? 'Add New Dashboard User' : 'Edit Dashboard User'}
              </h2>
              <button onClick={closeModal} className="close-button">
                ×
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Email</label>
                  <input 
                    type="email" 
                    value={form.email} 
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} 
                    required 
                    className="form-input"
                    placeholder="user@example.com"
                  />
                </div>

                <div className="form-field">
                  <label className="form-label">Full Name</label>
                  <input 
                    type="text" 
                    value={form.full_name} 
                    onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} 
                    required 
                    className="form-input"
                    placeholder="John Doe"
                  />
            </div>

                <div className="form-field">
                  <label className="form-label">Twilio Phone Number</label>
                  <input 
                    type="text" 
                    value={form.phone} 
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} 
                    className="form-input"
                    placeholder="e.g. +1234567890"
                  />
            </div>

                <div className="form-field">
                  <label className="form-label">
                    {isEditingImportedUser() 
                      ? 'Team' 
                      : `Teams ${form.role === 'team_lead' ? '(can select multiple)' : ''}`}
                  </label>
                  <Select
                    mode={isEditingImportedUser() 
                      ? undefined  // Imported users: always single selection, regardless of role
                      : (form.role === 'team_lead' ? 'multiple' : undefined)}
                    style={{ width: '100%' }}
                    placeholder={isEditingImportedUser() ? "Select team" : "Select team(s)"}
                    value={isEditingImportedUser() 
                      ? (form.team_ids && form.team_ids.length > 0 ? form.team_ids[0] : undefined)
                      : form.team_ids}
                    onChange={(value) => {
                      const isImported = isEditingImportedUser();
                      
                      if (isImported) {
                        // Imported users: single selection only (even if they have team_lead role)
                        // Force single value - take only the first value if array somehow passed
                        let singleValue;
                        if (Array.isArray(value)) {
                          singleValue = value.length > 0 ? value[0] : null;
                        } else {
                          singleValue = value || null;
                        }
                        const teamId = singleValue ? [singleValue] : [];
                        setForm(f => ({ ...f, team_ids: teamId }));
                      } else {
                        // Regular users: can select multiple if team_lead
                        let newTeamIds = Array.isArray(value) ? value : (value ? [value] : []);
                        // Validate: cannot select -1 (No Team) with other teams
                        if (newTeamIds.includes(-1) && newTeamIds.length > 1) {
                          // If -1 is selected with others, keep only -1
                          newTeamIds = [-1];
                        }
                        setForm(f => ({ ...f, team_ids: newTeamIds }));
                      }
                    }}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={-1}>No Team</Select.Option>
                    {teams.map(team => (
                      <Select.Option key={team.id} value={team.id}>
                        {team.name}
                      </Select.Option>
                    ))}
                  </Select>
                  {!isEditingImportedUser() && form.role === 'team_lead' && form.team_ids && form.team_ids.length > 1 && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Selected {form.team_ids.length} teams
                    </div>
                  )}
            </div>

                {!isEditingImportedUser() && (
                  <div className="form-field">
                    <label className="form-label">Role</label>
                    <select 
                      value={form.role} 
                      onChange={e => setForm(f => ({ ...f, role: e.target.value }))} 
                      className="form-select"
                    >
                      <option value="team_lead">Team Lead</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                )}

                {!isEditingImportedUser() && (
                  <div className="form-field">
                    <label className="form-label">
                      Password {isNew ? '' : '(leave blank to keep unchanged)'}
                    </label>
                    <input 
                      type="password" 
                      value={form.password} 
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))} 
                      className="form-input"
                      placeholder={isNew ? "Required" : "Leave blank to keep current"}
                      required={isNew}
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" onClick={closeModal} className="cancel-button">
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                  disabled={!hasFormChanges()}
                >
                  {isNew ? 'Add User' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="delete-modal-content">
              <div className="delete-icon">
                <svg width="24" height="24" fill="#ef4444" viewBox="0 0 24 24">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
              </div>
              <h3 className="delete-title">Delete User</h3>
              <p className="delete-message">
                Are you sure you want to delete <strong>{userToDelete.full_name}</strong>? 
                This action cannot be undone.
              </p>
              <div className="delete-actions">
                <button onClick={closeDeleteModal} className="cancel-button">
                  Cancel
                </button>
                <button onClick={handleDeleteConfirm} className="confirm-delete-button">
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Warning Modal */}
      {showSyncWarningModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="delete-modal-content">
              <div className="delete-icon">
                <svg width="24" height="24" fill="#f59e0b" viewBox="0 0 24 24">
                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
                </svg>
              </div>
              <h3 className="delete-title">Sync Organization</h3>
              <p className="delete-message">
                <strong>Warning:</strong> This action will overwrite all existing imported users data with fresh data from your organization. 
                Any manual changes made to imported users will be lost.
                <br/><br/>
                Are you sure you want to proceed with the sync?
              </p>
              <div className="delete-actions">
                <button onClick={closeSyncWarningModal} className="cancel-button">
                  Cancel
                </button>
                <button onClick={confirmSyncOrganization} className="confirm-delete-button">
                  Proceed with Sync
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Confirmation Modal */}
      {showSaveConfirmationModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="delete-modal-content">
              <div className="delete-icon">
                <svg width="24" height="24" fill="#10b981" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="delete-title">{isNew ? 'Add User' : 'Save Changes'}</h3>
              <p className="delete-message">
                {isNew 
                  ? 'Are you sure you want to add this new user?'
                  : 'Are you sure you want to save these changes to the user?'
                }
              </p>
              <div className="delete-actions">
                <button onClick={closeSaveConfirmationModal} className="cancel-button">
                  Cancel
                </button>
                <button onClick={confirmSave} className="confirm-delete-button">
                  {isNew ? 'Add User' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 